import { CSV_LIMITS, decodeCsv, detectDelimiter, parseCsv, type CsvDelimiter, type CsvEncoding, type ParsedCsv } from '@os/doc';

export type CsvReadOptions = { encoding?: CsvEncoding; delimiter?: CsvDelimiter };
export type CsvRead = ({ ok: true; delimiter: CsvDelimiter; encoding: CsvEncoding } & ParsedCsv) | { ok: false; reason: 'not_utf8' | 'too_large' };

/** Decode, find the delimiter (unless the person chose one) and parse: the work done off the main thread. */
export function readCsvBytes(bytes: Uint8Array, opts: CsvReadOptions = {}): CsvRead {
  const decoded = decodeCsv(bytes, opts.encoding);
  if (!decoded.ok) return decoded;
  const delimiter = opts.delimiter ?? detectDelimiter(decoded.text);
  return { ok: true, delimiter, encoding: decoded.encoding, ...parseCsv(decoded.text, delimiter) };
}

/**
 * Reads a chosen file in a Web Worker so a 5 MB file never freezes the page (backlog §17.2); runs inline where
 * workers are missing. Nothing leaves the browser.
 */
export async function readCsvFile(file: File, opts: CsvReadOptions = {}): Promise<CsvRead> {
  if (file.size > CSV_LIMITS.bytes) return { ok: false, reason: 'too_large' };
  const bytes = new Uint8Array(await file.arrayBuffer());
  if (typeof Worker === 'undefined') return readCsvBytes(bytes, opts);
  return new Promise<CsvRead>((resolve) => {
    const worker = new Worker(new URL('./csv.worker.ts', import.meta.url), { type: 'module' });
    const done = (r: CsvRead) => {
      worker.terminate();
      resolve(r);
    };
    worker.onmessage = (e: MessageEvent<CsvRead>) => done(e.data);
    worker.onerror = () => done(readCsvBytes(bytes, opts));
    worker.postMessage({ bytes, opts }); // copied, not transferred: the inline fallback still reads it
  });
}
